/**
 * The Display's "Now playing" face (2026-10-26, DEC-315): the song timeline playing on any computer, as a strip of
 * its sections, the section with the bar you are on in it and the beats, and a countdown before each change.
 *
 * Operator, 2026-10-26: "a display item (same item as already there) to show current timeline being played. 'no
 * timeline being played' when nothing playing. minuture version on the timeline with sections so user can see where
 * they are ... how many bars into the section it is and maybe 8 beats before the section is due to change change to a
 * countdown like CHORUS 8 7 6 5... etc 1". Their answers the same day: a loop counts down every time round; the
 * countdown names what is COMING; 4, 8 or 16 beats, 4 by default (`countdownBeats`); a section changes where Live
 * launches the scene, which with Live on 1 bar is the next bar line, as OPBridge lands every jump today.
 *
 * Reads only the timeline transport (`timeline/transport.ts`: what every playhead and Navigate's Now playing read), so
 * it needs no launcher and shows the same on every page. It follows the primary and each paired peer, as the count-in
 * rings do, and shows the primary's run first. Read-only: it sends nothing (DEC-161).
 *
 * The layout comes from the item's own size (container queries, app.css "Display: Now playing"), and a long name
 * shrinks to fit rather than being cut off. Paint is skin tokens (skin-components.css 11n, mirrored in design.html);
 * section colours are data: the one kind scheme (`timeline/sectionHue.ts`) the song row, the launcher and Navigate share. Study: `public/now-playing-study.html`.
 */

import { For, Show, createEffect, createMemo, createSignal, on, onCleanup, onMount } from 'solid-js';
import { leadInSectionId, timelineLoops, timelinePlaySections, type Timeline } from '@opbuttons/schema';
import { bridges, primaryBridge, resolveBridge } from '../opbridge/store.ts';
import { followTransport, transportBar, transportBeat, transportPending, transportRun } from '../timeline/transport.ts';
import { getTimeline } from '../timeline/store.ts';
import { sectionColour } from '../timeline/sectionHue.ts';
import { activeSkin } from '../settings/store.ts';
import type { DisplayItem } from '../layout/store.ts';

/** OPBridge counts four beats to every timeline bar, whatever the signature (reference: opbridge-timelines lib.rs). */
const BEATS_PER_BAR = 4;
const DEFAULT_COUNTDOWN = 4;

interface Band { id: string; label: string; short: string; color: string; start: number; len: number; rule: 'loop' | 'stop' | 'next' }

type DueKind = 'start' | 'jump' | 'next' | 'loop' | 'hold' | 'end';

interface View {
  state: 'queued' | 'playing' | 'paused' | 'held';
  name: string;
  machine: string;
  idx: number;
  sec: string;
  color: string;
  loop: boolean;
  /** Which time round of a looping section this is, counted since it was first seen (1 = not counted yet). */
  pass: number;
  bar: number;
  of: number;
  beat: number;
  /** Bars from the timeline's start for the playhead line; -1 hides it (a start still waiting). */
  head: number;
  cd: { digit: number; name: string; color: string; loop: boolean } | null;
  /** The band wearing the pending rim (-1 none). */
  tgt: number;
  pend: boolean;
  sub: string;
}

const KIND_ABBR: Record<string, string> = {
  intro: 'IN', verse: 'V', prechorus: 'PC', chorus: 'CH', bridge: 'BR', solo: 'SO', outro: 'OUT', drop: 'DR',
  breakdown: 'BD', instrumental: 'INS', build: 'BU', interlude: 'INT',
};
/** "PC" for PRE-CHORUS, "V2" for VERSE 2: what a band shows when its full name does not fit. */
function shortName(name: string): string {
  const num = (name.match(/\d+$/) ?? [''])[0];
  const kind = name.replace(/\d+$/, '').toLowerCase().replace(/[^a-z]/g, '');
  return (KIND_ABBR[kind] ?? name.replace(/[^A-Z]/g, '').slice(0, 3)) + num;
}
const sentence = (s: string) => (s && s === s.toUpperCase() ? s.charAt(0) + s.slice(1).toLowerCase() : s);
const ordinal = (n: number) => `${n}${n % 10 === 1 && n % 100 !== 11 ? 'st' : n % 10 === 2 && n % 100 !== 12 ? 'nd' : n % 10 === 3 && n % 100 !== 13 ? 'rd' : 'th'}`;
/** Drawn, not a glyph: JetBrains Mono has no U+21BB, and its fallback read as "u2" (the study, 2026-10-26). A
 *  function, so each use is its own node. */
const LoopIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-label="loops">
    <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" /><path d="M21 3v5h-5" />
  </svg>
);

/** The band holding bar `pos` (a position on an end line is in the next band, OPBridge's edge rule). */
function bandAt(bands: Band[], pos: number): number {
  let k = 0;
  while (k < bands.length - 1 && pos >= bands[k]!.start + bands[k]!.len - 1e-9) k++;
  return k;
}

/** A name never cut off on stage: past the width it has, it gets smaller (to 10 px) instead. */
function fitWidth(el: HTMLElement | undefined): void {
  if (!el) return;
  el.style.fontSize = '';
  if (!el.clientWidth || el.scrollWidth <= el.clientWidth + 0.5) return;
  const fs = parseFloat(getComputedStyle(el).fontSize);
  el.style.fontSize = `${Math.max(10, Math.floor((fs * el.clientWidth) / el.scrollWidth * 0.97))}px`;
  if (el.scrollWidth > el.clientWidth + 0.5) el.style.fontSize = `${Math.max(10, parseFloat(el.style.fontSize) - 1)}px`;
}

export function NowPlayingFace(props: { item: DisplayItem }) {
  // Every bridge a timeline can play on, the primary first.
  const targetIds = createMemo(() => {
    const ids = new Set<string>([primaryBridge()?.id ?? '']);
    for (const b of bridges()) if (b.bearer) ids.add(b.id);
    return [...ids];
  }, [], { equals: (a, b) => a.join(' ') === b.join(' ') });
  const followed = new Map<string, () => void>();
  createEffect(on(targetIds, (ids) => {
    const want = new Set(ids);
    for (const [id, release] of followed) if (!want.has(id)) { release(); followed.delete(id); }
    for (const id of want) if (!followed.has(id)) followed.set(id, followTransport(id));
  }));
  onCleanup(() => { for (const release of followed.values()) release(); followed.clear(); });

  /** The run to show: the primary's, else the first peer's. Changes with runs, not with positions. */
  const active = createMemo<{ target: string; t: Timeline; machine: string } | null>(() => {
    for (const id of targetIds()) {
      const run = transportRun(id);
      if (!run) continue;
      const t = getTimeline(run.timelineId);
      if (t) return { target: id, t, machine: resolveBridge(id)?.name ?? '' };
    }
    return null;
  }, null, { equals: (a, b) => a?.target === b?.target && a?.t === b?.t && a?.machine === b?.machine });

  const bands = createMemo<Band[]>(() => {
    const a = active();
    if (!a) return [];
    const lead = leadInSectionId(a.t);
    let start = 0;
    return timelinePlaySections(a.t).map((s, i) => {
      const len = Math.max(1, s.lengthBars | 0);
      const label = s.name || (s.id === lead ? 'LEAD-IN' : `PART ${i + 1}`);
      const onEnd = s.onEnd ?? 'auto-advance';
      const band: Band = { id: s.id, label, short: shortName(label), color: sectionColour(s), start, len, rule: onEnd === 'loop' ? 'loop' : onEnd === 'stop' ? 'stop' : 'next' };
      start += len;
      return band;
    });
  });
  const total = createMemo(() => bands().reduce((n, b) => n + b.len, 0));

  // ─── the frame: one view of the run, worked out every animation frame while one exists ───
  const [view, setView] = createSignal<View | null>(null);
  let pass = { idx: -1, within: 0, n: 1 };

  function compute(a: { target: string; t: Timeline; machine: string }): View | null {
    const bs = bands();
    const n = bs.length;
    const run = transportRun(a.target);
    if (!run || n === 0) return null;
    const at = transportBar(a.target, a.t);
    const pending = transportPending(a.target);
    const beatNow = transportBeat(a.target);
    const beats = props.item.countdownBeats ?? DEFAULT_COUNTDOWN;
    const runState = at?.state ?? run.state;
    const pos = at && runState !== 'queued' ? at.pos : run.parkedBar;
    const idx = bandAt(bs, pos);
    const cur = bs[idx]!;
    const within = Math.max(0, Math.min(cur.len, pos - cur.start));
    const held = runState === 'playing' && cur.rule === 'stop' && within >= cur.len - 1e-3;
    const state: View['state'] = runState === 'queued' ? 'queued' : runState === 'paused' ? 'paused' : held ? 'held' : 'playing';

    if (state === 'playing') {
      if (pass.idx !== idx) pass = { idx, within, n: 1 };
      else { if (within < pass.within - 0.25) pass.n++; pass.within = within; }
    } else if (state === 'queued') pass = { idx: -1, within: 0, n: 1 };

    // What changes next, and in how many beats.
    let due: { rem: number; idx: number; kind: DueKind } | null = null;
    if (state === 'queued') {
      if (pending?.kind === 'start' && pending.beat !== null && beatNow !== null) due = { rem: pending.beat - beatNow, idx: bandAt(bs, pending.bar), kind: 'start' };
    } else if (state === 'playing' || state === 'held') {
      if (pending?.kind === 'jump' && pending.beat !== null && beatNow !== null && pending.beat - beatNow > 0) {
        due = { rem: pending.beat - beatNow, idx: bandAt(bs, pending.bar), kind: 'jump' };
      } else if (state === 'playing') {
        const rem = (cur.len - within) * BEATS_PER_BAR;
        if (cur.rule === 'loop') due = { rem, idx, kind: 'loop' };
        else if (cur.rule === 'stop') due = { rem, idx: -1, kind: 'hold' };
        else if (idx + 1 < n) due = { rem, idx: idx + 1, kind: 'next' };
        else due = timelineLoops(a.t) ? { rem, idx: 0, kind: 'next' } : { rem, idx: -1, kind: 'end' };
      }
    }
    const counting = !!due && due.rem > 0 && due.rem <= beats + 1e-6;
    const tgtBand = due && due.idx >= 0 ? bs[due.idx]! : null;
    const cd = counting && due ? {
      digit: Math.max(1, Math.ceil(due.rem - 1e-6)),
      name: due.kind === 'end' ? 'END' : due.kind === 'hold' ? 'HOLD' : tgtBand!.label,
      color: tgtBand ? tgtBand.color : cur.color,
      loop: due.kind === 'loop',
    } : null;

    const S = sentence;
    let sub: string;
    if (cd && due) {
      sub = due.kind === 'loop' ? 'Loops unless you press'
        : due.kind === 'end' ? 'Then the timeline stops'
          : due.kind === 'hold' ? `${S(cur.label)} holds at its end`
            : due.kind === 'start' ? 'Starting'
              : due.idx === idx ? 'Again from its start'
                : `Next, after ${S(cur.label)}`;
    } else if (state === 'queued') sub = pending?.beat === null ? 'Waiting to start' : 'Starting at the next bar line';
    else if (state === 'held') sub = 'Waiting for a press';
    else if (state === 'paused') sub = 'Paused';
    else if (due?.kind === 'jump') sub = `${S(tgtBand?.label ?? '')} next`;
    else if (cur.rule === 'loop') sub = 'Loops until you move on';
    else if (cur.rule === 'stop') sub = 'Then it holds';
    else sub = idx + 1 < n ? `Then ${S(bs[idx + 1]!.label)}` : timelineLoops(a.t) ? `Then ${S(bs[0]!.label)}` : 'Then the timeline stops';

    return {
      state, name: a.t.name, machine: a.machine, idx, sec: cur.label, color: cur.color, loop: cur.rule === 'loop',
      pass: pass.n, bar: Math.min(cur.len, Math.floor(within) + 1), of: cur.len,
      beat: Math.min(BEATS_PER_BAR, Math.floor((within - Math.floor(within)) * BEATS_PER_BAR) + 1),
      head: state === 'queued' ? -1 : cur.start + within,
      cd, tgt: due && (due.kind === 'jump' || due.kind === 'start' || (due.kind === 'next' && counting)) ? due.idx : -1,
      pend: !counting && due?.kind === 'jump', sub,
    };
  }

  let raf = 0;
  const frame = () => {
    raf = 0;
    const a = active();
    setView(a ? compute(a) : null);
    if (a) raf = requestAnimationFrame(frame);
  };
  createEffect(on(active, (a) => {
    if (a && !raf) raf = requestAnimationFrame(frame);
    if (!a) { setView(null); pass = { idx: -1, within: 0, n: 1 }; }
  }));
  onCleanup(() => { if (raf) cancelAnimationFrame(raf); raf = 0; });

  // ─── fitting: band names by what fits, big names shrunk to fit; again on any resize ───
  let root: HTMLDivElement | undefined;
  let stripEl: HTMLDivElement | undefined;
  let secEl: HTMLSpanElement | undefined;
  let cdnEl: HTMLSpanElement | undefined;
  const [size, setSize] = createSignal(0);
  const [fit, setFit] = createSignal<string[]>([]);
  function fitLabels(): void {
    if (!stripEl) return;
    const out: string[] = [];
    for (const band of Array.from(stripEl.querySelectorAll<HTMLElement>('.dr-np-b'))) {
      const room = band.clientWidth - 4;
      const full = band.querySelector<HTMLElement>('[data-v="full"]');
      const short = band.querySelector<HTMLElement>('[data-v="short"]');
      out.push(room <= 0 ? 'none' : full && full.offsetWidth <= room ? 'full' : short && short.offsetWidth <= room ? 'short' : 'none');
    }
    const prev = fit();
    if (out.length !== prev.length || out.some((x, i) => x !== prev[i])) setFit(out);
  }
  onMount(() => {
    if (!root) return;
    const ro = new ResizeObserver(() => setSize((n) => n + 1));
    ro.observe(root);
    onCleanup(() => ro.disconnect());
  });
  const fitKey = createMemo(() => {
    const v = view();
    return v ? `${v.state}|${v.sec}|${v.cd?.name ?? ''}|${!!v.cd}|${v.loop}|${v.pass > 1}|${v.bar > 9}|${v.of}|${(v.cd?.digit ?? 0) > 9}` : '';
  });
  // A skin change swaps the face (Phosphor's is wider) without resizing the box, so it refits too; and the style (LCD
  // draws in mono). Measured on the next frame, once the new face's stylesheet values apply.
  createEffect(on([fitKey, size, bands, activeSkin, () => props.item.style], () => {
    fitWidth(secEl); fitWidth(cdnEl); fitLabels();
    requestAnimationFrame(() => { fitWidth(secEl); fitWidth(cdnEl); fitLabels(); });
  }));

  const headLeft = (v: View) => {
    const n = bands().length;
    const gaps = (n - 1) * 2;
    const band = bands()[v.idx];
    return `calc((100% - ${gaps}px) * ${(v.head / Math.max(1, total())).toFixed(5)} + ${band ? v.idx * 2 : 0}px)`;
  };
  const pips = createMemo(() => Array.from({ length: view()?.cd ? (props.item.countdownBeats ?? DEFAULT_COUNTDOWN) : BEATS_PER_BAR }, (_, i) => i),
    [], { equals: (a, b) => a.length === b.length });

  return (
    <div
      ref={root}
      class="dr-np"
      data-run={view()?.state ?? 'idle'}
      data-cd={view()?.cd ? 'true' : undefined}
      data-pend={view()?.pend ? 'true' : undefined}
      style={{ '--sc': view()?.color ?? 'transparent', '--tc': view()?.cd?.color ?? view()?.color ?? 'transparent' }}
    >
      <Show when={view()} fallback={<span class="dr-np-idle">No timeline playing</span>}>
        {(v) => (
          <div class="dr-np-live">
            <div class="dr-np-top"><span class="dr-np-tl">{v().name}</span><span class="dr-np-pc">{v().machine}</span></div>
            <div class="dr-np-mid">
              <div class="dr-np-now">
                <span class="dr-np-sec" ref={secEl}>{v().sec}</span>
                <Show when={v().loop}><span class="dr-np-loop">{LoopIcon()}<Show when={v().pass > 1}><b>{v().pass}</b></Show></span></Show>
              </div>
              <div class="dr-np-cnt">
                <b class="dr-np-bar">{v().state === 'queued' ? '-' : v().state === 'held' ? 'end' : String(v().bar)}</b>
                <span class="dr-np-of">{v().state === 'queued' || v().state === 'held' ? '' : `/${v().of}`}</span>
              </div>
              <div class="dr-np-cd" data-loop={v().cd?.loop ? 'true' : undefined}>
                <span class="dr-np-cdw"><span class="dr-np-cdn" ref={cdnEl}>{v().cd?.name ?? ''}</span><span class="dr-np-cdloop">{LoopIcon()}</span></span>
                <b class="dr-np-dig">{v().cd?.digit ?? ''}</b>
              </div>
            </div>
            <div class="dr-np-sub" title={v().pass > 1 ? `${ordinal(v().pass)} time round` : undefined}>{v().sub}</div>
            <div class="dr-np-pips">
              <For each={pips()}>{(i) => <i data-on={(v().cd ? i < v().cd!.digit : v().state === 'playing' && i < v().beat) ? 'true' : undefined} />}</For>
            </div>
            <div class="dr-np-strip" ref={stripEl}>
              <For each={bands()}>
                {(b, i) => (
                  <div
                    class="dr-np-b"
                    style={{ '--len': String(b.len), '--bc': b.color }}
                    data-st={i() < v().idx ? 'past' : i() === v().idx && v().state !== 'queued' ? 'now' : 'later'}
                    data-tgt={v().tgt === i() ? 'true' : undefined}
                    data-fit={fit()[i()] ?? 'none'}
                    title={`${sentence(b.label)}, ${b.len} bar${b.len === 1 ? '' : 's'}`}
                  >
                    <span class="dr-np-bl" data-v="full">{b.label}</span>
                    <span class="dr-np-bl" data-v="short">{b.short}</span>
                  </div>
                )}
              </For>
              <Show when={v().head >= 0}><i class="dr-np-ph" style={{ left: headLeft(v()) }} /></Show>
            </div>
          </div>
        )}
      </Show>
    </div>
  );
}
